import { FC, useMemo, useState } from 'react'
import { Button, Divider, Input, Select } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { translate } from '../../../core'
import { FormEntryField } from '../../../core/type'

const MIN_LENGTH = 3

export type WatchLookupSelectProps = FormEntryField<string> & {
  /** Hardcoded predefined values (kept unchanged as the seed list). */
  predefined: string[]
  /** Distinct values already persisted across existing watch records. */
  persisted?: string[]
  disabled?: boolean
}

/**
 * Searchable dropdown for the Watch form's Movement / Shape fields that also
 * lets users create a new value when the one they need doesn't exist.
 *
 * The existing list stays searchable via the Select's own search box; new
 * values are added through a dedicated input at the bottom of the dropdown
 * (antd's "add item" pattern), which is independent of the Select's internal
 * search state so the Create control always renders reliably.
 *
 * Options merge the predefined seed list with the values persisted in existing
 * watches (and anything created this session), deduped case-insensitively so
 * existing short codes (e.g. `A`, `SQ`) and the currently stored value always
 * remain selectable. A new value can only be created when it is at least three
 * characters (after trimming) and is not a case-insensitive duplicate.
 */
export const WatchLookupSelect: FC<WatchLookupSelectProps> = ({
  value,
  onChange,
  predefined,
  persisted = [],
  disabled,
}) => {
  const [draft, setDraft] = useState('')
  const [created, setCreated] = useState<string[]>([])

  const options = useMemo(() => {
    const result: { label: string; value: string }[] = []
    const seenLower = new Set<string>()
    const seenExact = new Set<string>()

    const push = (item: string) => {
      result.push({ label: item, value: item })
      seenLower.add(item.toLowerCase())
      seenExact.add(item)
    }

    // Predefined seed list: keep every entry exactly as-is — never drop a
    // predefined value, even ones that differ only in case (e.g. Round/ROUND).
    for (const candidate of predefined) {
      const trimmed = candidate?.trim()
      if (trimmed && !seenExact.has(trimmed)) push(trimmed)
    }

    // Persisted + session-created values: merge in, skipping case-insensitive
    // duplicates so a stored "round" doesn't repeat a predefined "Round".
    const addUnique = (candidate?: string | null) => {
      const trimmed = candidate?.trim()
      if (trimmed && !seenLower.has(trimmed.toLowerCase())) push(trimmed)
    }
    persisted.forEach(addUnique)
    created.forEach(addUnique)

    // Always render the currently stored value exactly, even if it only differs
    // in case from an existing option, so legacy values display correctly.
    const current = value?.trim()
    if (current && !seenExact.has(current)) push(current)

    return result
  }, [predefined, persisted, created, value])

  const term = draft.trim()
  const isDuplicate = term.length > 0 && options.some(o => o.value.toLowerCase() === term.toLowerCase())
  const isTooShort = term.length > 0 && term.length < MIN_LENGTH
  const canCreate = term.length >= MIN_LENGTH && !isDuplicate

  const handleCreate = () => {
    if (!canCreate) return
    setCreated(prev => [...prev, term])
    onChange?.(term)
    setDraft('')
  }

  const hint = isDuplicate
    ? translate('value_already_exists')
    : isTooShort
      ? translate('min_characters')
      : ''

  return (
    <Select
      className='w-100'
      showSearch
      allowClear
      disabled={disabled}
      value={value ?? undefined}
      options={options}
      optionFilterProp='label'
      onChange={next => onChange?.(next || undefined)}
      onDropdownVisibleChange={open => !open && setDraft('')}
      dropdownRender={menu => (
        <>
          {menu}
          <Divider style={{ margin: '4px 0' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '4px 12px 8px' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
              <Input
                style={{ flex: 1 }}
                placeholder={translate('add_new_value')}
                value={draft}
                status={isTooShort || isDuplicate ? 'warning' : undefined}
                onChange={event => setDraft(event.target.value)}
                // Keep keystrokes inside the input so the Select doesn't hijack
                // them for option navigation; Enter creates the value.
                onKeyDown={event => {
                  event.stopPropagation()
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    handleCreate()
                  }
                }}
              />
              <Button
                type='primary'
                icon={<PlusOutlined />}
                disabled={!canCreate}
                onClick={handleCreate}
              >
                {translate('create')}
              </Button>
            </div>
            {hint && <span style={{ color: '#faad14', fontSize: 12 }}>{hint}</span>}
          </div>
        </>
      )}
    />
  )
}
